import { Card, Typography, Progress, Row, Col, Spin } from 'antd';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Clock, ChevronRight } from 'lucide-react';
import { AppRoutes } from 'src/enums/ERoutes';
import ResellerDashboard from '../ResellerDashboard/ResellerDashboard';
import { ResellerSiderItem } from 'src/enums/IResellerSiderItems';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getResellerAcademy } from '@/store/resellerAcademy/selector';
import './ResellerAcademy.scss';
import { useEffect, useRef, useState } from 'react';
import ResellerTour from '../ResellerDashboard/ResellerTour/ResellerTour';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { getResellerData } from '@/store/reseller/selector';
import { setTour } from '@/store/resellerTour/slice';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateTourStatus } from '@/api/reseller';
import { getResellerTourState } from '@/store/resellerTour/selector';
import AcademySearch from '@/components/AlgoliaSearch/AcademySearch';

const { Title, Text } = Typography;

const ResellerAcademy = () => {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const resellerData = useAppSelector(getResellerData);
	const queryClient = useQueryClient();
	const [searchLoading, setSearchLoading] = useState(false);

	const { chapters, loading } = useAppSelector(getResellerAcademy);
	const academyTourRef1 = useRef<HTMLDivElement>(null);
	const academyTourRef2 = useRef<HTMLDivElement>(null);

	const academyTourSteps = [
		{
			title: <h1 className="font-bold text-2xl">Video Tutorials</h1>,
			description: (
				<p className="text-base">
					Our Academy has everything you need to get started video guides, step-by-step
					walkthroughs, and full product tutorials, all in one place.
				</p>
			),
			target: academyTourRef1.current as HTMLElement,
		},
		{
			title: <h1 className="font-bold text-2xl">Step-by-step Tutorials</h1>,
			description: (
				<p className="text-base">
					You will also find step-by-step tutorials about all the features we offer you to be a
					successful reseller.
				</p>
			),
			target: academyTourRef2.current as HTMLElement,
		},
	];

	const updateToursMutation = useMutation({
		mutationFn: updateTourStatus,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['reseller'] });
		},
	});

	const resellerTour = useAppSelector(getResellerTourState);

	useEffect(() => {
		if (!resellerData?.emailAddress || resellerTour.open) {
			return;
		}

		if (!resellerData.tours?.academy) {
			updateToursMutation.mutate('academy');
			dispatch(
				setTour({
					open: true,
					currentStep: 0,
					returnTo: AppRoutes.RESELLER_ACADEMY,
				}),
			);
		}
	}, [resellerData?.emailAddress, resellerTour.open]);

	const getChapterProgress = (chapterId: number): number => {
		const chapter = chapters?.find((c) => c.id === chapterId);
		if (!chapter) {
			return 0;
		}

		const isTutorialsChapter = chapter.tutorials && chapter.tutorials.length > 0;
		const hasVideos = chapter.videos && chapter.videos.length > 0;
		const hasPdfs = chapter.pdfs && chapter.pdfs.length > 0;

		// If it's a tutorials chapter, calculate progress based on tutorials
		if (isTutorialsChapter && chapter.tutorials) {
			const completedTutorials = chapter.tutorials?.filter((t) => t.isCompleted).length || 0;
			return Math.round((completedTutorials / chapter.tutorials?.length) * 100);
		}

		// If there are no videos but there are PDFs, calculate progress based on PDFs
		if (!hasVideos && hasPdfs && chapter.pdfs) {
			const completedPdfs = chapter.pdfs.filter((p) => p.isCompleted).length;
			return Math.round((completedPdfs / chapter.pdfs.length) * 100);
		}

		// Default to video progress if videos exist
		if (hasVideos) {
			const completedVideos = chapter.videos.filter((v) => v.isCompleted).length;
			return Math.round((completedVideos / chapter.videos.length) * 100);
		}

		return 0;
	};

	const handleSearchReset = () => {
		setSearchLoading(false);
	};

	return (
		<ResellerDashboard activeKey={ResellerSiderItem.ACADEMY} showHeader={false}>
			<Spin spinning={loading}>
				<div className="reseller-academy">
					<div className="reseller-academy__container">
						<div className="reseller-academy__header">
							<Title level={2} className="reseller-academy__title">
								Reseller Academy
							</Title>
							<Text className="reseller-academy__subtitle">
								Master the art of reselling with our comprehensive training program
							</Text>
						</div>

						{/* Search Component */}
						<div className="reseller-academy__search">
							<AcademySearch isLoading={searchLoading} onReset={handleSearchReset} />
						</div>

						<Row gutter={[24, 24]}>
							{chapters?.map((chapter) => (
								<Col
									ref={
										chapter.id === 1 ? academyTourRef1 : chapter.id === 8 ? academyTourRef2 : null
									}
									xs={24}
									md={12}
									lg={8}
									key={chapter.id}
								>
									<Card
										hoverable
										className="reseller-academy__card"
										onClick={() =>
											navigate(
												AppRoutes.RESELLER_ACADEMY_CHAPTER.replace(
													':chapterId',
													chapter.id?.toString(),
												),
											)
										}
									>
										<div className="reseller-academy__card-content">
											<div className="reseller-academy__card-header">
												<Text className="reseller-academy__chapter-number">
													Chapter {chapter.id}
												</Text>
												<ChevronRight className="reseller-academy__chevron" />
											</div>

											<Progress
												percent={getChapterProgress(chapter.id)}
												strokeColor={{
													'0%': '#1890ff',
													'100%': '#52c41a',
												}}
												strokeWidth={8}
												className="reseller-academy__progress"
											/>

											<Title level={4} className="reseller-academy__card-title">
												{chapter.title}
											</Title>

											<Text className="reseller-academy__card-description">
												{chapter.description}
											</Text>

											<div className="reseller-academy__card-footer">
												<div className="reseller-academy__stat">
													<BookOpen className="reseller-academy__icon" />
													<span>
														{(chapter.tutorials?.length
															? chapter.tutorials?.length
															: chapter.videos.length
																? chapter.videos.length
																: chapter.pdfs?.length) || 0}{' '}
														lessons
													</span>
												</div>
												<div className="reseller-academy__stat">
													<Clock className="reseller-academy__icon" />
													<span>{chapter.totalDuration || 10} mins</span>
												</div>
											</div>
										</div>
									</Card>
								</Col>
							))}
						</Row>
					</div>
				</div>
			</Spin>
			<ResellerTour tourSteps={academyTourSteps} />
		</ResellerDashboard>
	);
};

export default ResellerAcademy;
